import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ReactComponent as Dropdown } from '../../../../assets/images/dropdown-arrow.svg';
import { SEARCH_MORE_FILTER_OPTIONS, SEARCH_FILTER_OPTIONS } from '../../../../constants/searchDummyData';
import DateRangeSelector from '../../Analytics/DateRangeSelector';

const dropdownTriggerSx = (hasValue) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
    height: '32px',
    padding: '0  12px',
    borderRadius: '8px',
    border: `1px solid ${hasValue ? '#148DFD' : '#D5DCE1'}`,
    background: '#FFF',
    boxShadow: '0px 1px 2px 0px rgba(6, 21, 34, 0.05)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
        borderColor: hasValue ? '#148DFD' : '#C5CED6',
        background: '#FFF',
    },
});

const dropdownMenuSx = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    zIndex: 100,
    minWidth: '120px',
    borderRadius: '8px',
    border: '1px solid #D5DCE1',
    background: '#FFF',
    boxShadow: '0px 15px 20px 0px rgba(6, 21, 34, 0.05)',
    padding: '8px',
    maxHeight: '300px',
    overflowY: 'auto',
};

const dropdownItemSx = (isSelected) => ({
    padding: '8px',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    color: isSelected ? '#061522' : '#405261',
    cursor: 'pointer',
    borderRadius: '8px',
    background: isSelected ? '#F9FAFA' : 'transparent',
    '&:hover': {
        background: '#F9FAFA',
        color: '#061522',
    },
});

const FilterButton = ({ label, onClick, width }) => {
    return (
        <Button
            onClick={onClick}
            sx={{ ...dropdownTriggerSx(false), width: width || 'auto', justifyContent: 'center' }}
            disableRipple
        >
            <Typography sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label}
            </Typography>
        </Button>
    );
};

const MoreDropdown = ({ selectedFilters, onToggleFilter, width }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <Box ref={ref} sx={{ position: 'relative', width }}>
            <Button onClick={() => setOpen(!open)} sx={{ ...dropdownTriggerSx(false), width: '100%' }} disableRipple>
                <Typography sx={{ color: '#405261', fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, lineHeight: '18px' }}>
                    More
                </Typography>
                <Dropdown />
            </Button>
            {open && (
                <Box sx={{ ...dropdownMenuSx, minWidth: '160px' }}>
                    {SEARCH_MORE_FILTER_OPTIONS.map((option) => (
                        <Box
                            key={option}
                            sx={dropdownItemSx(selectedFilters.includes(option))}
                            onClick={() => {
                                onToggleFilter(option);
                                setOpen(false);
                            }}
                        >
                            {option}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const SortDropdown = ({ value, onChange, width }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const hasValue = value && SEARCH_FILTER_OPTIONS.sortOptions.includes(value);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <Box ref={ref} sx={{ position: 'relative', width }}>
            <Button onClick={() => setOpen(!open)} sx={{ ...dropdownTriggerSx(false), gap: '8px', width: '100%', minWidth: '120px' }} disableRipple>
                <Typography sx={{ color: hasValue ? '#061522' : '#405261', fontFamily: 'Inter', fontSize: '14px',  width:  hasValue ? '90px' : '51px', fontWeight: 400, lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {hasValue ? value : 'Sort by'}
                </Typography>
                <Dropdown />
            </Button>
            {open && (
                <Box sx={{ ...dropdownMenuSx, right: -17, left: 'auto', minWidth: '152px' }}>
                    {SEARCH_FILTER_OPTIONS.sortOptions.map((option) => (
                        <Box
                            key={option}
                            sx={dropdownItemSx(value === option)}
                            onClick={() => {
                                onChange(option);
                                setOpen(false);
                            }}
                        >
                            {option}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const SearchFilters = ({ filters, onFilterChange, onAddFilter, openCustomDatePicker = false, onCustomDatePickerOpenChange }) => {
    const { moreFilters, sortBy, timeframe, customStartDate, customEndDate } = filters;

    const handleAddFilter = (type) => {
        if (onAddFilter) {
            onAddFilter(type);
        }
    };

    const handleCustomDateChange = (range) => {
        if (range?.startDate) onFilterChange('customStartDate', range.startDate);
        if (range?.endDate) onFilterChange('customEndDate', range.endDate);
    };

    const handleCustomDatePickerOpenChange = (open) => {
        onCustomDatePickerOpenChange?.(open);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <FilterButton
                    label="Assigned"
                    onClick={() => handleAddFilter('assignee')}
                    width="96px"
                />
                <FilterButton
                    label="Status"
                    onClick={() => handleAddFilter('status')}
                    width="80px"
                />
                <FilterButton
                    label="Tags"
                    onClick={() => handleAddFilter('tags')}
                    width="70px"
                />
                {/* <FilterButton
                    label="Type"
                    onClick={() => handleAddFilter('type')}
                    width="64px"
                /> */}
                <FilterButton
                    label="Timeframe"
                    onClick={() => handleAddFilter('timeframe')}
                    width="104px"
                />
                {timeframe === 'custom' && (
                    <DateRangeSelector
                        dateRange={{ startDate: customStartDate, endDate: customEndDate }}
                        onDateChange={handleCustomDateChange}
                        ignoreJoiningDate
                        variant="search"
                        open={openCustomDatePicker}
                        onOpenChange={handleCustomDatePickerOpenChange}
                    />
                )}
                {/* <MoreDropdown
                    selectedFilters={moreFilters}
                    onToggleFilter={(val) => handleAddFilter(val)}
                    width="96px"
                /> */}
                <SortDropdown
                    value={sortBy}
                    onChange={(val) => onFilterChange('sortBy', val)}
                    width="104px"
                />
            </Box>
        </Box>
    );
};

export default SearchFilters;
